import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import Label from '../../components/form/Label';
import { api, apiEndpoints } from '../../utils/apiService';
import SalesPlaybookTab from './SalesPlaybookTab';

const SalesSettingsPage: React.FC = () => {
  const [auditCallTargetCount, setAuditCallTargetCount] = useState('7');
  const [automationLoading, setAutomationLoading] = useState(true);
  const [automationSaving, setAutomationSaving] = useState(false);
  const [automationMessage, setAutomationMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadAutomationSettings = async () => {
      setAutomationLoading(true);
      setAutomationMessage('');
      try {
        const response = await api.get(
          apiEndpoints.salesConfigAutomationSettings(),
        );
        if (!response || !response.ok) {
          throw new Error(
            `Failed to load automation settings${response ? ` (${response.status})` : ''}.`,
          );
        }
        const data = await response.json();
        if (!cancelled) {
          setAuditCallTargetCount(String(data.audit_call_target_count ?? 7));
        }
      } catch (err) {
        if (!cancelled) {
          setAutomationMessage(
            err instanceof Error
              ? err.message
              : 'Failed to load automation settings.',
          );
        }
      } finally {
        if (!cancelled) setAutomationLoading(false);
      }
    };
    loadAutomationSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveAutomationSettings = async () => {
    const parsed = Number(auditCallTargetCount);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 30) {
      setAutomationMessage(
        'Audit call target must be a whole number between 1 and 30.',
      );
      return;
    }

    setAutomationSaving(true);
    setAutomationMessage('');
    try {
      const response = await api.patch(
        apiEndpoints.salesConfigAutomationSettings(),
        {
          audit_call_target_count: parsed,
        },
      );
      if (!response || !response.ok) {
        const body = response ? await response.text() : '';
        throw new Error(
          `Failed to save automation settings${response ? ` (${response.status})` : ''}. ${body}`,
        );
      }
      const data = await response.json();
      setAuditCallTargetCount(String(data.audit_call_target_count ?? parsed));
      setAutomationMessage('Automation settings saved.');
    } catch (err) {
      setAutomationMessage(
        err instanceof Error
          ? err.message
          : 'Failed to save automation settings.',
      );
    } finally {
      setAutomationSaving(false);
    }
  };

  return (
    <>
      <PageMeta
        title="AskArthur | Sales Settings"
        description="Configure the sales playbook, campaign strategy, and revenue assumptions"
      />

      <div className="space-y-5">
        <div>
          <Link
            to="/sales/prospects"
            className="text-sm font-medium text-[rgb(var(--app-muted))] hover:text-[rgb(var(--app-brand-strong))]"
          >
            ← Back to prospects
          </Link>
        </div>

        <div>
          <h1 className="app-page-title">Sales Settings</h1>
          <p className="app-page-subtitle mt-1.5">
            Tune the sales playbook, campaign stack ranking, and revenue
            assumptions used by the research and drip agents.
          </p>
        </div>

        <ComponentCard title="Automation" collapsible>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="max-w-xs">
              <Label htmlFor="audit-call-target-count">
                Audit calls before research
              </Label>
              <input
                id="audit-call-target-count"
                type="number"
                min={1}
                max={30}
                value={auditCallTargetCount}
                disabled={automationLoading}
                onChange={(event) =>
                  setAuditCallTargetCount(event.target.value)
                }
                className="app-input h-11 w-full rounded-2xl px-4 py-2.5 text-sm"
              />
              <p className="mt-2 text-xs text-[rgb(var(--app-muted))]">
                Arthur schedules the next daily audit until this many calls have
                completed, then starts research. Stalled provider results are
                polled automatically and force-failed after a timeout so the
                chain keeps moving.
              </p>
            </div>
            <button
              type="button"
              onClick={saveAutomationSettings}
              disabled={automationLoading || automationSaving}
              className="app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              {automationSaving ? 'Saving…' : 'Save automation'}
            </button>
          </div>
          {automationMessage && (
            <p className="mt-3 text-sm text-[rgb(var(--app-muted))]">
              {automationMessage}
            </p>
          )}
        </ComponentCard>

        <SalesPlaybookTab />
      </div>
    </>
  );
};

export default SalesSettingsPage;
